/// <amd-module name="TFS/GlobalBanner/GlobalMessagesService" />
import {
    GlobalMessagesChangedEvents,
    IGlobalDialog,
    IGlobalMessageAction,
    IGlobalMessageBanner,
    IGlobalMessagesService,
    IToast
} from "TFS/GlobalBanner/GlobalMessagesService.Types";
import { IVssPageContext, ServiceOptions, Services, VssObservableService, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IPostRequestEvent, requestListeners } from "VSS/Platform/Fetch";
import { ISettingsRestClient } from "VSS/Platform/RestClient/Settings";
import { UserClaims, userHasClaim } from "VSS/Platform/UserClaims";

// Export to cover the typeinfo for consumers of the non-types webproj
export {
    GlobalMessagesChangedEvents,
    GlobalMessageLevel,
    IGlobalDialog,
    IGlobalMessageAction,
    IGlobalMessageBanner,
    IGlobalMessageLink,
    IGlobalMessagesService,
    IToast
} from "TFS/GlobalBanner/GlobalMessagesService.Types";

interface IGlobalMessagesData {
    actions: IGlobalMessageAction[];
    banners: IGlobalMessageBanner[];
    dialogs: IGlobalDialog[];
}

const globalDialogSharedDataKey = "globalDialogData";
const globalDialogDismissedSettingPrefix = "GlobalDialog/Dismissed/";
const globalMessagesSharedDataKey = "globalMessageData";
const globalBannerDismissedSettingPrefix = "GlobalMessage/Dismissed/";

class GlobalMessagesService
    extends VssObservableService<IGlobalMessageBanner | IGlobalDialog | IToast | undefined>
    implements IGlobalMessagesService {
    private currentDialogs: IGlobalDialog[];
    private currentMessageBanners: IGlobalMessageBanner[];

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const data = contributionService.getSharedData<IGlobalMessagesData>(globalMessagesSharedDataKey);
        this.currentMessageBanners = data && data.banners ? data.banners : [];

        const dialogData = contributionService.getSharedData<IGlobalMessagesData>(globalDialogSharedDataKey);
        this.currentDialogs = dialogData && dialogData.dialogs ? dialogData.dialogs : [];

        requestListeners.subscribe(this.handleEndRequest, "afterRequest");
        document.body.addEventListener("legacyGlobalMessage", this.handleLegacyGlobalMessage);
    }

    public _serviceEnd(pageContext: IVssPageContext) {
        requestListeners.unsubscribe(this.handleEndRequest, "afterRequest");
        document.body.removeEventListener("legacyGlobalMessage", this.handleLegacyGlobalMessage);
    }

    public addToast(toast: IToast) {
        this._notify(toast, GlobalMessagesChangedEvents.GlobalToastChangedEvent);
    }

    public getDialog(): IGlobalDialog | undefined {
        return this.currentDialogs[0];
    }

    public addDialog(dialog: IGlobalDialog, closeExisting?: boolean, onlyIfHigherLevel?: boolean): void {
        if (onlyIfHigherLevel) {
            const currentDialog = this.currentDialogs[0];
            if (currentDialog && (currentDialog.priority || 0) >= (dialog.priority || 0)) {
                // Currently displayed dialog is at least as important. No-op.
                return;
            }
        }

        if (closeExisting) {
            this.currentDialogs.shift();
        }
        this.currentDialogs.unshift(dialog);
        this._notify(dialog, GlobalMessagesChangedEvents.GlobalDialogChangedEvent);
    }

    public closeDialog(): void {
        // Write a setting indicating that this dialog was dismissed
        const currentDialog = this.getDialog();
        if (currentDialog && currentDialog.settingId) {
            const settingEntries: { [key: string]: any } = {};
            settingEntries[globalDialogDismissedSettingPrefix + currentDialog.settingId] = true;

            if(userHasClaim(this.pageContext, UserClaims.Member)) {
                const settingsClient = this.pageContext.getRestClient<ISettingsRestClient>("ISettingsRestClient");
                settingsClient.setEntries(settingEntries, "me");
            }
        }

        // Set the new dialog as undefined
        this.currentDialogs.shift();
        this._notify(this.currentDialogs[0], GlobalMessagesChangedEvents.GlobalDialogChangedEvent);
    }

    public getBanner(): IGlobalMessageBanner | undefined {
        return this.currentMessageBanners[0];
    }

    public addBanner(banner: IGlobalMessageBanner, closeExisting?: boolean, onlyIfHigherLevel?: boolean): void {
        if (onlyIfHigherLevel) {
            const currentBanner = this.currentMessageBanners[0];
            if (currentBanner && (currentBanner.level || 0) >= (banner.level || 0)) {
                // Currently displayed banner is at least as important. No-op.
                return;
            }
        }

        if (closeExisting) {
            this.currentMessageBanners.shift();
        }
        this.currentMessageBanners.unshift(banner);
        this._notify(banner, GlobalMessagesChangedEvents.GlobalMessageChangedEvent);
    }

    public getActions(): IGlobalMessageAction[] | undefined {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const data = contributionService.getSharedData<IGlobalMessagesData>(globalMessagesSharedDataKey);
        return data && data.actions ? data.actions : undefined;
    }

    public closeBanner(): void {
        // Write a setting indicating that this message was dismissed
        const currentMessage = this.getBanner();
        if (currentMessage && currentMessage.settingId) {
            const settingEntries: { [key: string]: any } = {};
            settingEntries[globalBannerDismissedSettingPrefix + currentMessage.settingId] = true;

            const settingsClient = this.pageContext.getRestClient<ISettingsRestClient>("ISettingsRestClient");
            settingsClient.setEntries(settingEntries, "me");
        }

        // Set the new message as undefined
        this.currentMessageBanners.shift();
        this._notify(this.currentMessageBanners[0], GlobalMessagesChangedEvents.GlobalMessageChangedEvent);
    }

    private handleEndRequest = (postRequestEvent: IPostRequestEvent) => {
        if (postRequestEvent.response && postRequestEvent.response.headers) {
            const globalMessageWarningHeader = postRequestEvent.response.headers.get("X-VSS-GlobalMessage");
            if (globalMessageWarningHeader) {
                try {
                    const banner = JSON.parse(globalMessageWarningHeader);
                    this.addBanner(banner, false, true);
                } catch (ex) {
                    console.warn("Failed to deserialize X-VSS-GlobalMessage header: " + ex);
                }
            }
        }
    };

    private handleLegacyGlobalMessage = (messageEvent: CustomEvent) => {
        this.addBanner(messageEvent.detail, false, true);
    };
}

Services.add("IGlobalMessagesService", { serviceFactory: GlobalMessagesService });

/**
 * Service exposing TFS data from the current page to external content
 */
class ExternalGlobalMessagesService extends VssService {
    public addBanner(banner: IGlobalMessageBanner): void {
        const globalMessageSvc = this.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
        globalMessageSvc.addBanner(banner);
    }

    public addDialog(dialog: IGlobalDialog): void {
        const globalMessageSvc = this.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
        globalMessageSvc.addDialog(dialog);
    }

    public addToast(toast: IToast) {
        const globalMessageSvc = this.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
        const newToast = {
            ...toast,
            onCallToActionClick: () => {
                if (toast.onCallToActionClick) {
                    toast.onCallToActionClick();
                }
            }
        };
        globalMessageSvc.addToast(newToast);
    }

    // Here for compat.
    public setGlobalMessageBanner(banner: IGlobalMessageBanner): void {
        this.addBanner(banner);
    }

    public closeBanner(): void {
        const globalMessageSvc = this.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
        globalMessageSvc.closeBanner();
    }

    public closeDialog(): void {
        const globalMessageSvc = this.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
        globalMessageSvc.closeDialog();
    }
}

Services.add("ms.vss-tfs-web.tfs-global-messages-service", { serviceFactory: ExternalGlobalMessagesService, options: ServiceOptions.Remotable });
